import { Observable } from 'rxjs';
import createJobQueue from './JobQueue.mjs';

/**
 * @param {Window} _window browser global execution context
 * @param {String} sdkVersion of the SDK
 * @returns {LogService} An instance of a log service
 * @module Utils.LogService
 */
function createLogService(util, environment, sdkVersion, _window) {
    const schemeDomainPortRegex = /https:\/\/[^/]+/g;
    const pathRegex = /\/app\/c\/js\/|\/app\/js\//g;
    const versionRegex = /\?v=[^:]+|&v=[^:]+/g;
    const spaceRegex = /\n\s*|\\n\s*/g;
    const appVersionRegex = /[?&]+(appVersion=[a-zA-Z0-9.\-_]{1,20})/g;
    const firstWordRegex = /\w+\s/g;
    const timers = {};
    const jobQueue = createJobQueue({ window: _window, jobsPerSec: 4 });

    function serverCorePath() {
        let result = '';
        if (environment?.apiUrlBase) {
            result += environment?.apiUrlBase;
        }
        result += '/activityLog/entries';
        if (environment?.partnerId) {
            result += '?partnerId=' + environment?.partnerId;
        }
        return result;
    }

    function serverClientLogPath() {
        let result = '';
        if (environment?.webUrlBase) {
            result += environment?.webUrlBase;
        }
        result += '/clientLog';
        if (environment?.partnerId) {
            result += '?partnerId=' + environment?.partnerId;
        }
        return result;
    }

    function send(logObject) {
        return new Promise(function(resolve, reject) {
            let req = new XMLHttpRequest();
            req.open('post', serverCorePath());
            req.setRequestHeader('Content-Type', 'application/json');
            req.setRequestHeader('AntiCSRF', 'AntiCSRF');
            req.withCredentials = true;
            if (environment?.oauthToken) {
                req.setRequestHeader('Authorization', 'Bearer ' + environment?.oauthToken);
            } else if (environment?.sessionId) {
                req.setRequestHeader('Authorization', 'SeedonkSession ' + environment?.sessionId);
            }
            req.onload = function() {
                resolve();
            };
            req.onerror = function() {
                reject();
            };
            req.onabort = function() {
                reject();
            };
            req.send(JSON.stringify(logObject));
        });
    }

    function clientLog(logObject) {
        return new Promise(function(resolve, reject) {
            let req = new XMLHttpRequest();
            req.open('post', serverClientLogPath());
            req.setRequestHeader('Content-Type', 'application/json');
            req.setRequestHeader('AntiCSRF', 'AntiCSRF');
            req.withCredentials = true;
            if (environment?.oauthToken) {
                req.setRequestHeader('Authorization', 'Bearer ' + environment?.oauthToken);
            } else if (environment?.sessionId) {
                req.setRequestHeader('Authorization', 'SeedonkSession ' + environment?.sessionId);
            }
            req.onload = function() {
                resolve();
            };
            req.onerror = function() {
                reject();
            };
            req.onabort = function() {
                reject();
            };
            req.send(JSON.stringify(logObject));
        });
    }

    function errorTypeName(error) {
        const name = Object.prototype.toString.call(error);
        return name.replaceAll('[', '').replaceAll(']', '').replace(firstWordRegex, '');
    }

    /**
     * @param {Error} error Could be any kind of Error
     * @returns {String}
     */
    function errorMessage(error) {
        // client errors with a stack have to be compressed
        if (error?.stack) {
            let result = error.stack.replace(schemeDomainPortRegex, '');
            result = result.replace(pathRegex, '');
            result = result.replace(versionRegex, '');
            result = result.replace(spaceRegex, ' ');
            result = result.replaceAll('/app', '');
            result = result.replaceAll('/lib/sdk/videoSdk.js', 'sdk');
            result = result.replaceAll('.min.js', '');
            result = result.replaceAll('.js', '');
            return result;
        }

        // browser, platform or media errors
        if (error) {
            let result = errorTypeName(error) + ' ';
            if (error.code !== undefined && error.code !== null) {
                result += error.code + ' ';
            }
            if (error.message) {
                result += error.message + ' ';
            }
            return result;
        }

        return '';
    }

    function clientInfoField(value) {
        if (typeof value === 'string' && value !== '') {
            return util.removeInvalidCharacters(value).substring(0, 30) + ' ';
        }
        return '';
    }

    /**
     * @returns {String} A description of the current browser in a format more compact than JSON
     */
    function clientInfoToLog() {
        let result = '';
        result += clientInfoField(environment?.client?.browser?.name);
        result += clientInfoField(environment?.client?.browser?.version);
        result += clientInfoField(environment?.client?.os?.name);
        result += clientInfoField(environment?.client?.os?.version);
        result += clientInfoField(environment?.client?.platform?.type);
        result += clientInfoField(environment?.client?.platform?.vendor);
        return result;
    }

    function getLogType(params) {
        if (params && 'issue,info'.indexOf(params.type) > -1) {
            return params.type;
        }
        return 'issue';
    }

    function getDescr(params) {
        let result = '';
        if (params.descr) {
            result += params.descr;
        }

        // Record the error response from the server for failed Axios API calls
        if (params.error?.response?.data) {
            result += JSON.stringify(params.error.response.data);
        }

        if (params && params.object) {
            if (result.length > 0 && !result.endsWith(' ')) {
                result += ' ';
            }
            result += JSON.stringify(params.object);
        }
        if (result === '') {
            return;
        }
        return result;
    }

    function getData1(params) {
        try {
            // If the int value was specified and greater than 0, then use that value
            if (params?.int && typeof params?.int === 'number' && params?.int > 0) {
                return Math.floor(params.int);
            }

            // If there is an error which is an Axios error, then use the status code of the response
            if (typeof params?.error?.response?.status === 'number') {
                return Math.floor(params?.error?.response?.status);
            }
        } catch (ignore) {
            // ignore
        }
    }

    function getData2(params) {
        let result = sdkVersion + ' ';
        if (params.type !== 'info') {
            const exception = params.error || new Error();
            result += errorMessage(exception);
        }
        if (!result.endsWith(' ')) {
            result += ' ';
        }

        // If the error happens to be an Axios error, then include more info useful for debugging
        const axiosErrorConfig = params?.error?.config;
        if (axiosErrorConfig) {
            if (axiosErrorConfig?.url) {
                result += 'url:' + axiosErrorConfig?.url;
            }
            if (!result.endsWith(' ')) {
                result += ' ';
            }
            if (axiosErrorConfig?.params) {
                result += 'params:' + axiosErrorConfig?.params;
            }
            if (!result.endsWith(' ')) {
                result += ' ';
            }
        }

        result += clientInfoToLog();
        if (result === '') {
            return;
        }
        return result;
    }

    function getClientType() {
        if (environment?.client?.os?.name) {
            return util.removeInvalidCharacters(environment.client.os.name) // restrict character set
                .toLowerCase() // normalize spelling for search
                .substring(0, 30); // limit characters
        }
        return 'browser';
    }

    function getClientAppVersion() {
        if (_window && _window.document) {
            if (_window.SeedonkAndroid || (_window?.webkit?.messageHandlers)) {
                let version = _window.document.location.href.match(appVersionRegex);
                if (Array.isArray(version) && version.length > 0 && version[0].length > 12) {
                    return version[0].substring(12); // remove the prefix, '?appVersion=' or '&appVersion='
                }
                return 'embedded';
            }
            return 'browser';
        }
    }

    function getTraceId(params) {
        if (params?.traceId) {
            return util.removeInvalidCharacters(params.traceId)?.substring(0, 40);
        }
        if (params?.error) {
            return util.removeInvalidCharacters(params.error?.traceId)?.substring(0, 40);
        }
    }

    // clientAppId is the domain in reverse order
    function getClientAppId() {
        try {
            return util.removeInvalidCharacters(_window?.document?.location?.hostname)
                .split('.')
                .reverse()
                .join('.')
                .substring(0, 40);
        } catch (ignore) {
            // ignore
        }
    }

    /**
     * @typedef ActivityLog
     * @description A structure used to post logs
     * @property {String} [type="issue"] Possible values 'issue' (default), 'info'.
     * Truncated to 30 characters.
     * @property {String} [module] Name of the component or module that is creating the log.
     * Maps to the 'action' field of the API. Truncated to 30 characters.
     * @property {String} [code] A short, queryable name of the specific error with no context-specific info.
     * It can be used along with module to identify the line of code that created the log. Truncated to 30 characters.
     * @property {String} [descr] A longer description of the specific error type,
     * and may contain context-specific info such as object Ids.
     * Can be up to 1024 characters, but normally should be much smaller.
     * @property {Object} [object] An object that will be serialized and included in the API descr field.
     * @property {Object} [error] An Error object that will be serialized and included in the API descr field.
     * @property {Number} [int] Integer value only. Used to track performance data such as milliseconds.
     * Maps to the data1 field in the API.
     * @property {String} [deviceId] the related device, if any
     * @property {String} [traceId] A traceId to log. This overrides params.error.traceId if both values exist,
     * otherwise params.error.traceId will be used.
     */

    /**
     * @description Posts a to the server but returns an Observable that emits and completes
     *     when the log has successfully posted to the server.
     * @param {ActivityLog} params The specification object for a log post
     * @returns {Observable} Emits when the log has been sent, or error if the log was throttled or otherwise failed.
     * @name logObservable
     * @method
     */
    function logObservable(params) {
        if (!params) {
            return new Observable(subscriber => subscriber.error('bad params'));
        }
        return new Observable(subscriber => {
            const logData = {
                logType: getLogType(params),
                category: 'web',
                action: util.removeInvalidCharacters(params.module).substring(0, 30),
                code: util.removeInvalidCharacters(params.code).substring(0, 30),
                descr: getDescr(params),
                deviceId: util.removeInvalidCharacters(params.deviceId).substring(0, 30),
                data1: getData1(params),
                data2: getData2(params),
                clientType: getClientType(params),
                clientAppName: environment?.partnerId,
                clientAppId: getClientAppId(),
                clientAppVersion: getClientAppVersion(),
                traceId: getTraceId(params)
            };
            if (logData.deviceId === '' || logData.deviceId === undefined || logData.deviceId === null) {
                delete logData.deviceId;
            }
            jobQueue.add(send, logData).subscribe({
                next: result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                error: e => {
                    subscriber.error(e);
                }
            });
        });
    }

    function noop() {
        // do nothing
    }

    /**
     * @description  Posts a log but does not return anything.
     *     This is "fire and forget" without being concerned
     *     if the log was successfully sent or not, and this is what
     *     clients should use most of the time.
     * @param {ActivityLog} params The specification object for a log post
     * @name log
     * @method
     */
    function log(params) {
        logObservable(params).subscribe({
            next: noop,
            error: noop
        });
    }

    /**
     * @description Mark the beginning of a performance log
     * @param {String} key Value to identify the item to be logged
     * @name startPerformanceLog
     * @method
     */
    function startPerformanceLog(key) {
        if (_window.performance && _window.performance.now) {
            timers[key] = _window.performance.now();
        }
    }

    /**
     * @description Mark the ending of a performance log and send the log
     * @param {String} key Value to identify the item to be logged, matching the key used in startPerformanceLog
     * @param {object|undefined} params A specification object for the log function. If undefined, the log will be cancelled.
     * @name endPerformanceLog
     * @method
     */
    function endPerformanceLog(key, params) {
        if (_window.performance && _window.performance.now && timers[key] && params) {
            const endTime = _window.performance.now();
            params.int = Math.round(endTime - timers[key]);
            params.type = 'info';
            log(params);
        }
        delete timers[key];
    }

    // https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event
    function handleError(event, source, lineno, colno, error) {
        try {
            // https://developer.mozilla.org/en-US/docs/Web/API/ErrorEvent

            // "message" may be a string or ErrorEvent depending on how the
            // error was listened to -> window.onerror vs window.addEventListener
            if (typeof event === 'string') {
                // window.onerror style
                let descr = event + ' ';
                if (source) {
                    descr += source + ' ';
                }
                if (lineno) {
                    descr += lineno;
                    if (colno) {
                        descr += ':' + colno + ' ';
                    } else {
                        descr += ' ';
                    }
                }
                log({
                    type: 'issue',
                    module: 'activityLog',
                    code: 'javascript',
                    descr: descr,
                    error: (error && error instanceof Error) ? error : new Error(descr)
                });
                return true; // this form requires returning true to cancel the error
            }

            if (event && (event instanceof Error || event.message)) {
                // window.addEventListener style
                let descr = '';
                if (event.message) {
                    descr += event.message + ' ';
                }
                if (event.filename) {
                    descr += event.filename + ' ';
                }
                if (event.lineno) {
                    descr += event.lineno;
                    if (event.colno) {
                        descr += ':' + event.colno + ' ';
                    } else {
                        descr += ' ';
                    }
                }
                log({
                    type: 'issue',
                    module: 'activityLog',
                    code: 'javascript',
                    descr: descr,
                    error: (event && event.error && event.error instanceof Error)
                        ? event.error
                        : new Error(descr)
                });
                return false; // this form requires returning false to cancel the error
            }

            log({
                type: 'issue',
                module: 'activityLog',
                code: 'javascript',
                descr: 'unknown',
                error: new Error('unknown')
            });

        } catch (ignore) {
            /* do nothing */
        }
    }

    // default global error handler
    _window.addEventListener('error', handleError);

    // mobile console will produce issues on the server
    _window.console = (function(builtInConsole) {
        function handleLog(logParam) {
            if (builtInConsole.log && logParam) {
                builtInConsole.log(logParam);
            }

            let logValue = '';
            if (logParam instanceof Error) {
                logValue = (logParam.name ? logParam.name : '')
                    + ' ' + (logParam.message ? logParam.message : '')
                    + ' ' + (logParam.lineNumber ? logParam.lineNumber : '')
                    + ':' + (logParam.columnNumber ? logParam.columnNumber : '');
            } else if (logParam instanceof Object) {
                try {
                    logValue = JSON.stringify(logParam);
                } catch (e) {
                    logValue = 'object';
                }
            } else if (typeof logParam === 'string') {
                logValue = logParam;
            } else {
                logValue = 'none';
            }

            const logConfig = {
                type: 'issue',
                module: 'activityLog',
                code: 'console',
                descr: logValue,
                error: (logParam instanceof Error) ? logParam : undefined
            };
            // log to Activity Log
            log(logConfig);
            // log to System.out on the server, and logs can be found in catalina.out
            // This is especially useful when there is no auth. Will not log in prod.
            clientLog(logConfig).then(util.noop, util.noop);
        }

        // mobile browsers do not have _window.console
        if (!_window.console
                || _window?.webkit?.messageHandlers
                || _window.SeedonkAndroid) {
            return {
                log: handleLog,
                info: handleLog,
                warn: handleLog,
                error: handleLog
            };
        }
        return _window.console;

    }(_window.console));

    return Object.freeze({
        log: log,
        logObservable: logObservable,
        startPerformanceLog: startPerformanceLog,
        endPerformanceLog: endPerformanceLog
    });
}

export default createLogService;
